import { useEffect, useState } from "react";
import { HelpCircle, Timer, PlayCircle, Images, ArrowRight } from "lucide-react";
import { fileUrl } from "@/lib/upload";

const H = ({ icon: Icon, children }) => <h2 className="text-base md:text-lg font-semibold uppercase tracking-widest text-[var(--lp-accent)] flex items-center gap-2"><Icon className="h-5 w-5" /> {children}</h2>;

export function TextImage({ b }) {
  const img = fileUrl(b.image_file_id);
  return (
    <section className="max-w-5xl mx-auto px-6 py-12 grid gap-8 lg:grid-cols-2 items-center" data-testid={`block-${b.id}`}>
      <div className={b.image_side === "left" && img ? "lg:order-2" : ""}>
        {b.title && <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-white">{b.title}</h2>}
        {b.text && <p className="mt-4 text-white/85 whitespace-pre-line leading-relaxed">{b.text}</p>}
      </div>
      {img && <img src={img} alt={b.title || ""} className="rounded-2xl w-full object-cover max-h-96 border border-white/10" />}
    </section>
  );
}

const embed = (u) => {
  const m = (u || "").match(/(?:youtu\.be\/|v=|shorts\/)([\w-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  const v = (u || "").match(/vimeo\.com\/(\d+)/);
  return v ? `https://player.vimeo.com/video/${v[1]}` : null;
};

export function Video({ b }) {
  const src = embed(b.video_url);
  if (!src && !b.video_url) return null;
  return (
    <section className="max-w-5xl mx-auto px-6 py-12" data-testid={`block-${b.id}`}>
      {b.title && <H icon={PlayCircle}>{b.title}</H>}
      <div className="mt-6 rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video">
        {src ? <iframe title={b.title || "video"} src={src} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen /> : <video src={b.video_url} controls className="w-full h-full" />}
      </div>
    </section>
  );
}

export function Faq({ b }) {
  const [open, setOpen] = useState(0);
  if (!b.items?.length) return null;
  return (
    <section className="max-w-5xl mx-auto px-6 py-12" data-testid={`block-${b.id}`}>
      <H icon={HelpCircle}>{b.title || "Domande frequenti"}</H>
      <div className="mt-6 space-y-2">
        {b.items.map((it, i) => (
          <div key={it.id || `${b.id}-faq-${i}`} className="rounded-xl bg-white/5 border border-white/10">
            <button type="button" onClick={() => setOpen(open === i ? -1 : i)} className="w-full text-left px-4 py-3 font-semibold text-white flex justify-between gap-3" data-testid={`faq-${b.id}-${i}`}>{it.q}<span className="opacity-60">{open === i ? "−" : "+"}</span></button>
            {open === i && <p className="px-4 pb-4 text-sm text-white/80 whitespace-pre-line">{it.a}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");
export function Countdown({ b, d }) {
  const target = new Date(d.status === "Programmato" ? d.start_datetime : d.end_datetime).getTime();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const diff = Math.max(0, Math.floor((target - now) / 1000));
  const parts = [[Math.floor(diff / 86400), "giorni"], [Math.floor((diff % 86400) / 3600), "ore"], [Math.floor((diff % 3600) / 60), "min"], [diff % 60, "sec"]];
  return (
    <section className="max-w-5xl mx-auto px-6 py-12 text-center" data-testid={`block-${b.id}`}>
      <p className="text-base md:text-lg font-semibold uppercase tracking-widest text-[var(--lp-accent)] flex items-center justify-center gap-2"><Timer className="h-5 w-5" /> {b.title || (d.status === "Programmato" ? "Il concorso inizia tra" : "Il concorso termina tra")}</p>
      <div className="mt-6 flex justify-center gap-3 sm:gap-5">
        {parts.map(([v, l]) => <div key={l} className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3 min-w-[72px]"><p className="font-display font-black text-3xl sm:text-4xl text-white tabular-nums">{pad(v)}</p><p className="text-[11px] uppercase tracking-wider text-white/60">{l}</p></div>)}
      </div>
      {b.date_label && <p className="mt-4 text-sm text-white/70">{b.date_label}</p>}
    </section>
  );
}

export function Gallery({ b }) {
  if (!b.images?.length) return null;
  return (
    <section className="max-w-5xl mx-auto px-6 py-12" data-testid={`block-${b.id}`}>
      {b.title && <H icon={Images}>{b.title}</H>}
      <div className="mt-6 grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
        {b.images.map((id) => <img key={id} src={fileUrl(id)} alt="" className="rounded-xl w-full aspect-square object-cover border border-white/10" />)}
      </div>
    </section>
  );
}

export function ButtonBlock({ b, onCta }) {
  const primary = b.style !== "outline";
  const cls = primary ? "bg-[var(--lp-accent)] text-slate-900" : "bg-white/15 border border-white/30 text-white";
  const inner = <><span>{b.label || "Scopri di più"}</span><ArrowRight className="h-5 w-5" /></>;
  return (
    <section className="max-w-5xl mx-auto px-6 py-8 text-center" data-testid={`block-${b.id}`}>
      {b.url ? <a href={b.url} target={/^https?:/.test(b.url) ? "_blank" : undefined} rel="noreferrer" className={`inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold text-base shadow-xl hover:scale-[1.03] transition-transform ${cls}`}>{inner}</a>
        : <button type="button" onClick={onCta} className={`inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold text-base shadow-xl hover:scale-[1.03] transition-transform ${cls}`}>{inner}</button>}
    </section>
  );
}
